// src/components/AdminTickets.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminTickets.css';

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // ✅ बरोबर - /api शिवाय फक्त base URL
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    loadTickets();
  }, [statusFilter, pagination.page]);

  const loadTickets = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      // ✅ /api prefix सह योग्य URL
      const response = await axios.get(
        `${API_URL}/api/tickets?${params.toString()}`,
        getAuthHeaders()
      );
      
      if (response.data.success) {
        setTickets(response.data.data.items || []);
        setPagination({
          ...pagination,
          total: response.data.data.pagination.total,
          totalPages: response.data.data.pagination.totalPages
        });
      }
    } catch (err) {
      console.error('Error loading tickets:', err);
      setError(err.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadTicketDetails = async (ticketId) => {
    setLoading(true);
    try {
      // ✅ /api prefix सह योग्य URL
      const response = await axios.get(
        `${API_URL}/api/tickets/${ticketId}`,
        getAuthHeaders()
      );
      if (response.data.success) {
        setSelectedTicket(response.data.data);
      }
    } catch (err) {
      console.error('Error loading ticket details:', err);
      setError(err.response?.data?.message || 'Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClick = (ticket) => {
    loadTicketDetails(ticket.id);
  };

  const handleReply = async (ticketId) => {
    if (!replyText.trim()) return;

    try {
      // ✅ /api prefix सह योग्य URL
      const response = await axios.post(
        `${API_URL}/api/tickets/${ticketId}/replies`,
        { message: replyText },
        getAuthHeaders()
      );
      if (response.data.success) {
        setReplyText('');
        await loadTicketDetails(ticketId);
        await loadTickets();
      }
    } catch (err) {
      console.error('Error adding reply:', err);
      alert(err.response?.data?.message || 'Failed to add reply');
    }
  };

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      const message = prompt('Add a message (optional):');
      // ✅ /api prefix सह योग्य URL
      const response = await axios.patch(
        `${API_URL}/api/tickets/${ticketId}`,
        { 
          status: newStatus,
          message: message || ''
        },
        getAuthHeaders()
      );
      if (response.data.success) {
        await loadTicketDetails(ticketId);
        await loadTickets();
        alert('Ticket status updated successfully!');
      }
    } catch (err) {
      console.error('Error updating ticket:', err);
      alert(err.response?.data?.message || 'Failed to update ticket');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: '#3b82f6',
      in_progress: '#f59e0b',
      resolved: '#10b981',
      closed: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusBadge = (status) => {
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTickets = tickets.filter((ticket) => {
    const subject = ticket.subject || '';
    const ticketNo = ticket.ticket_no || '';
    const candidateName = ticket.candidate_name || '';

    const search = searchTerm.toLowerCase();
    return (
      subject.toLowerCase().includes(search) ||
      ticketNo.toLowerCase().includes(search) ||
      candidateName.toLowerCase().includes(search)
    );
  });

  const stats = {
    total: tickets.length,
    open: tickets.filter((t) => t.status === 'open').length,
    inProgress: tickets.filter((t) => t.status === 'in_progress').length,
    resolved: tickets.filter((t) => t.status === 'resolved').length,
    closed: tickets.filter((t) => t.status === 'closed').length
  };

  if (loading && tickets.length === 0) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading tickets...</p>
      </div>
    );
  }

  return (
    <div className="admin-tickets-container">

      {/* Page Header */}
      <div className="admin-page-header">
        <div className="page-title-section">
          <div className="page-title-icon">
            <i className="fas fa-ticket-alt"></i>
          </div>
          <div>
            <h1>Ticket Management</h1>
            <p>Manage candidate support tickets and conversations</p>
          </div>
        </div>

        <div className="ticket-stats">
          <div className="stat-card total-stat">
            <div className="stat-icon">
              <i className="fas fa-ticket-alt"></i>
            </div>
            <div>
              <span className="stat-label">Total</span>
              <strong>{stats.total}</strong>
            </div>
          </div>
          <div className="stat-card open-stat">
            <div className="stat-icon">
              <i className="fas fa-folder-open"></i>
            </div>
            <div>
              <span className="stat-label">Open</span>
              <strong>{stats.open}</strong>
            </div>
          </div>
          <div className="stat-card progress-stat">
            <div className="stat-icon">
              <i className="fas fa-spinner"></i>
            </div>
            <div>
              <span className="stat-label">In Progress</span>
              <strong>{stats.inProgress}</strong>
            </div>
          </div>
          <div className="stat-card resolved-stat">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div>
              <span className="stat-label">Resolved</span>
              <strong>{stats.resolved}</strong>
            </div>
          </div>
          <div className="stat-card closed-stat">
            <div className="stat-icon">
              <i className="fas fa-lock"></i>
            </div>
            <div>
              <span className="stat-label">Closed</span>
              <strong>{stats.closed}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <i className="fas fa-exclamation-circle"></i>
          {error}
          <button onClick={loadTickets}>Retry</button>
        </div>
      )}

      {/* Filters */}
      <div className="ticket-toolbar">
        <div className="filter-section">
          <label htmlFor="ticket-status">
            <i className="fas fa-filter"></i>
            Status
          </label>
          <select
            id="ticket-status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Tickets</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="search-section">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by ticket ID, subject or candidate..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className="clear-search"
              onClick={() => setSearchTerm('')}
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <button
          className="refresh-btn"
          onClick={loadTickets}
          disabled={loading}
        >
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync'}`}></i>
          Refresh
        </button>
      </div>

      {/* Main Content */}
      {tickets.length === 0 && !loading ? (
        <div className="empty-state">
          <div className="empty-icon">
            <i className="fas fa-ticket-alt"></i>
          </div>
          <h2>No tickets yet</h2>
          <p>When candidates raise support tickets, they will appear here.</p>
          <div className="empty-info">
            <i className="fas fa-info-circle"></i>
            Waiting for candidate tickets
          </div>
        </div>
      ) : filteredTickets.length === 0 && !loading ? (
        <div className="empty-state filtered-empty">
          <div className="empty-icon">
            <i className="fas fa-search"></i>
          </div>
          <h2>No matching tickets</h2>
          <p>Try changing the status filter or search keyword.</p>
          <button
            type="button"
            className="reset-filter-btn"
            onClick={() => {
              setStatusFilter('all');
              setSearchTerm('');
            }}
          >
            <i className="fas fa-redo"></i>
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="tickets-workspace">

          {/* Ticket List */}
          <div className="tickets-panel">
            <div className="panel-header">
              <div>
                <h3>Tickets</h3>
                <span>
                  {filteredTickets.length}{' '}
                  {filteredTickets.length === 1 ? 'ticket' : 'tickets'}
                </span>
              </div>
              <div className="panel-header-icon">
                <i className="fas fa-list"></i>
              </div>
            </div>

            <div className="ticket-list">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`ticket-item ${
                    selectedTicket?.id === ticket.id ? 'active' : ''
                  }`}
                  onClick={() => handleTicketClick(ticket)}
                >
                  <div className="ticket-item-top">
                    <span className="ticket-number">
                      {ticket.ticket_no || ticket.id}
                    </span>
                    <span
                      className="status-badge"
                      style={{ background: getStatusColor(ticket.status) }}
                    >
                      <span className="status-dot"></span>
                      {getStatusBadge(ticket.status)}
                    </span>
                  </div>

                  <h4>{ticket.subject || 'No subject'}</h4>

                  <div className="candidate-row">
                    <span>
                      <i className="fas fa-user"></i>
                      {ticket.candidate_name || 'Candidate'}
                    </span>
                    <span className="category-tag">
                      <i className="fas fa-tag"></i>
                      {ticket.category || 'general'}
                    </span>
                  </div>

                  <div className="ticket-item-footer">
                    <span>
                      <i className="far fa-clock"></i>
                      {formatDate(ticket.created_at)}
                    </span>
                    <span>
                      <i className="far fa-comment"></i>
                      {ticket.replies?.length || 0}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPagination({...pagination, page: pagination.page - 1})}
                  disabled={pagination.page === 1}
                >
                  <i className="fas fa-chevron-left"></i> Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPagination({...pagination, page: pagination.page + 1})}
                  disabled={pagination.page === pagination.totalPages}
                >
                  Next <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </div>

          {/* Ticket Details */}
          {selectedTicket ? (
            <div className="ticket-details-panel">
              <div className="details-header">
                <div className="details-title">
                  <div className="details-ticket-icon">
                    <i className="fas fa-ticket-alt"></i>
                  </div>
                  <div>
                    <span className="details-ticket-id">
                      {selectedTicket.ticket_no || selectedTicket.id}
                    </span>
                    <h2>{selectedTicket.subject || 'No subject'}</h2>
                  </div>
                </div>

                <div className="details-status">
                  <label>Status</label>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => updateTicketStatus(selectedTicket.id, e.target.value)}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              {/* Ticket Information */}
              <div className="ticket-information">
                <div className="information-item">
                  <span className="information-label">
                    <i className="fas fa-user"></i>
                    Candidate
                  </span>
                  <strong>{selectedTicket.candidate_name || 'N/A'}</strong>
                </div>

                <div className="information-item">
                  <span className="information-label">
                    <i className="fas fa-mobile-alt"></i>
                    Mobile
                  </span>
                  <strong>{selectedTicket.candidate_mobile || 'N/A'}</strong>
                </div>

                <div className="information-item">
                  <span className="information-label">
                    <i className="fas fa-tag"></i>
                    Category
                  </span>
                  <strong>{selectedTicket.category || 'general'}</strong>
                </div>

                <div className="information-item">
                  <span className="information-label">
                    <i className="far fa-calendar"></i>
                    Created
                  </span>
                  <strong>{formatDate(selectedTicket.created_at)}</strong>
                </div>
              </div>

              {/* Description */}
              <div className="description-section">
                <div className="section-heading">
                  <div className="section-heading-icon">
                    <i className="fas fa-align-left"></i>
                  </div>
                  <h3>Description</h3>
                </div>
                <div className="description-content">
                  {selectedTicket.description || 'No description provided.'}
                </div>
              </div>

              {/* Conversation */}
              <div className="conversation-section">
                <div className="section-heading conversation-heading">
                  <div className="section-heading-left">
                    <div className="section-heading-icon">
                      <i className="fas fa-comments"></i>
                    </div>
                    <div>
                      <h3>Conversation</h3>
                      <span>
                        {selectedTicket.replies?.length || 0}{' '}
                        {selectedTicket.replies?.length === 1 ? 'reply' : 'replies'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="replies-list">
                  {selectedTicket.replies && selectedTicket.replies.length > 0 ? (
                    selectedTicket.replies.map((reply, index) => (
                      <div key={reply.id || index} className="reply-item">
                        <div className="reply-avatar">
                          {(reply.author_name || 'A').charAt(0).toUpperCase()}
                        </div>
                        <div className="reply-content">
                          <div className="reply-header">
                            <strong>{reply.author_name || 'Admin'}</strong>
                            <span className="reply-role">
                              {reply.author_role || 'admin'}
                            </span>
                            <span className="reply-time">
                              {formatDate(reply.created_at)}
                            </span>
                          </div>
                          <p>{reply.message}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-replies">
                      <i className="far fa-comment-dots"></i>
                      <span>No replies yet</span>
                    </div>
                  )}
                </div>

                {/* Reply */}
                {selectedTicket.status !== 'closed' && (
                  <div className="reply-box">
                    <div className="reply-box-header">
                      <span>
                        <i className="fas fa-reply"></i>
                        Reply to candidate
                      </span>
                    </div>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply here..."
                      rows="4"
                      disabled={loading}
                    />
                    <div className="reply-box-footer">
                      <span>
                        <i className="fas fa-info-circle"></i>
                        Reply will be added to the conversation
                      </span>
                      <button
                        type="button"
                        onClick={() => handleReply(selectedTicket.id)}
                        disabled={!replyText.trim() || loading}
                      >
                        {loading ? (
                          <><i className="fas fa-spinner fa-spin"></i> Sending...</>
                        ) : (
                          <><i className="fas fa-paper-plane"></i> Send Reply</>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {selectedTicket.status === 'closed' && (
                  <div className="closed-ticket-message">
                    <i className="fas fa-lock"></i>
                    This ticket is closed. Reopen it to continue the conversation.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="no-ticket-selected">
              <div className="select-ticket-icon">
                <i className="fas fa-hand-pointer"></i>
              </div>
              <h3>Select a ticket</h3>
              <p>Select a ticket from the list to view its details and conversation.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminTickets;